import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import loyaltyService from '../../services/loyaltyService';
import EnrollmentModal from '../common/EnrollmentModal';
import { useAuth } from '../../context/AuthContext';

// Custom blue marker icon
const blueIcon = new L.Icon({
  iconUrl: '/img/blue_mapcon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MembershipDetails = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { org } = useAuth();
  const [group, setGroup] = useState(null);
  const [availableOffers, setAvailableOffers] = useState([]);
  const [earnableOffers, setEarnableOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locationsWithDistance, setLocationsWithDistance] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState(null);
  const [isMember, setIsMember] = useState(true);
  const [enrollmentModal, setEnrollmentModal] = useState({ open: false, location: null });

  useEffect(() => {
    if (redeemSuccess && countdown === null) {
      setCountdown(10);
    }
  }, [redeemSuccess, countdown]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (redeemSuccess && redeemModalOpen) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [redeemSuccess, redeemModalOpen]);
  const handleEnrollClick = (location) => {
    setEnrollmentModal({ open: true, location });
  };

  const handleEnrollSuccess = async () => {
    // Refresh the membership details after successful enrollment
    await fetchCompanyDetails();
  };

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      // Fetch group details directly using the new endpoint
      const response = await loyaltyService.getGroupDetails(companyId);

      if (!response) {
        throw new Error('Membership not found');
      }

      setIsMember(response.group.member_info.is_member);
      setGroup(response.group);
      setAvailableOffers(response.group.offers.filter(offer => !offer.point_value || response.group.member_info.current_points >= offer.point_value));
      setEarnableOffers(response.group.offers.filter(offer => offer.point_value && response.group.member_info.current_points < offer.point_value));
      setLoading(false);
    } catch (err) {
      // Check if it's a 404 error
      if (err.response && err.response.status === 404) {
        setError('Unable to find loyalty program');
      } else {
        setError(err.message || 'Failed to load membership details');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        (error) => {
          console.log('Could not get user location:', error);
        }
      );
    }
  }, [companyId]);

  useEffect(() => {
    if (group?.locations && userLocation) {
      // Calculate distances for all locations
      const locationsWithDist = group.locations
        .map(location => ({
          ...location,
          distance: location.lat && location.lon ? calculateDistance(
            userLocation.lat,
            userLocation.lon,
            location.lat,
            location.lon
          ) : 100000 // Default to a large distance if no coordinates
        }))
        .sort((a, b) => a.distance - b.distance);

      setLocationsWithDistance(locationsWithDist);
    } else if (group?.locations && !userLocation) {
      // If no user location, just use all locations without distance
      setLocationsWithDistance(group.locations);
    }
  }, [group, userLocation]);

  const handleBack = () => {
    navigate('/memberships');
  };

  const handleRedeemClick = (offer) => {
    setSelectedOffer(offer);
    setRedeemModalOpen(true);
    setRedeemError(null);
    setRedeemSuccess(null);
  };

  const handleCloseModal = () => {
    // Prevent closing during countdown
    if (countdown > 0) return;

    setRedeemModalOpen(false);
    setSelectedOffer(null);
    setRedeemError(null);
    setRedeemSuccess(null);
    setRedeeming(false);
    setCountdown(null);
  };

  const handleLeaveProgramClick = () => {
    setLeaveDialogOpen(true);
    setLeaveError(null);
  };

  const handleCloseLeaveDialog = () => {
    setLeaveDialogOpen(false);
    setLeaveError(null);
  };

  const handleConfirmLeave = async () => {
    setLeaving(true);
    setLeaveError(null);

    try {
      await loyaltyService.leaveProgram(group.id);
      // Navigate back to dashboard after successful leave
      navigate('/memberships');
    } catch (err) {
      setLeaveError(err.response?.data?.message || err.response?.data?.error || 'Failed to leave loyalty program');
    } finally {
      setLeaving(false);
    }
  };

  const handleConfirmRedeem = async () => {
    if (!selectedOffer) return;

    setRedeeming(true);
    setRedeemError(null);

    try {
      const response = await loyaltyService.redeemCoupon(group.id, selectedOffer.id, selectedOffer.customer_coupon_id);
      fetchCompanyDetails();
      setRedeemSuccess(response.redemption);

      // Update points locally immediately
      if (response.redemption.point_value) {
        setGroup(prevGroup => ({
          ...prevGroup,
          member_info: {
            ...prevGroup.member_info,
            current_points: prevGroup.member_info.current_points - response.redemption.point_value
          }
        }));
      } else if (selectedOffer.customer_coupon_id) {
        // Remove the redeemed offer from available offers
        setAvailableOffers(prevOffers => prevOffers.filter(offer => offer.customer_coupon_id !== selectedOffer.customer_coupon_id));
      }
    } catch (err) {
      setRedeemError(err.response?.data?.message || err.response?.data?.error || 'Failed to redeem coupon');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The loyalty program you're looking for may have been removed or you may not have access to it.
            </Typography>
            <Button variant="contained" onClick={handleBack}>
              Back to Memberships
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        {!org && (
          <IconButton onClick={handleBack} sx={{ mb: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          {group.logo && (
            <Box
              component="img"
              src={group.logo}
              alt={`${group.name} logo`}
              sx={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                mr: 3
              }}
            />
          )}
          <Box>
            <Typography variant="h4" gutterBottom>
              {group.name} Membership
            </Typography>
            {isMember ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MonetizationOnIcon sx={{ color: 'gold', mr: 1 }} />
                <Typography variant="h5">
                  {Math.floor(group.member_info.current_points).toLocaleString()} Coins
                </Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleEnrollClick(group)}
              >
                Sign up now
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {availableOffers.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
              Available Offers
            </Typography>

            <Grid container spacing={2}>
              {availableOffers.map((offer) => (
                <Grid size={{ xs: 12, md: 6 }} key={offer.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {offer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {!!offer.point_value && (
                            <>
                              <MonetizationOnIcon sx={{ color: 'primary.main', fontSize: 20, mr: 0.5 }} />
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                {offer.point_value} coins
                              </Typography>
                            </>)}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {offer.description}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            {offer.days_remaining && (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                {offer.days_remaining} days remaining. Expires on {new Date(offer.expire_when).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>

                          {group.allow_self_redemption && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleRedeemClick(offer)}
                            >
                              Redeem
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {earnableOffers.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
              Earnable Offers
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Earnable offers are subject to change without notice. Please check back frequently for new offers.
            </Typography>

            <Grid container spacing={2}>
              {earnableOffers.map((offer) => (
                <Grid size={{ xs: 12, md: 6 }} key={offer.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div" sx={{ color: '#999' }}>
                          {offer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <MonetizationOnIcon sx={{ color: 'warning.main', fontSize: 20, mx: 0.5 }} />
                          <Typography variant="body2" color="warning.main" fontWeight="bold">
                            {offer.point_value} coins
                          </Typography>
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {offer.description}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            {offer.days_remaining && (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                {offer.days_remaining} days remaining. Expires on {new Date(offer.expire_when).toLocaleDateString()}
                              </Typography>
                            )}

                            <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                              You need to earn {Math.ceil(offer.point_value - group.member_info.current_points)} more coins to redeem this offer.
                            </Typography>
                          </Box>

                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}


        {/* Locations Map Section */}
        {locationsWithDistance && locationsWithDistance.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Store Locations
            </Typography>

            <Box sx={{ height: 400 }}>
              <MapContainer
                bounds={calculateMapBounds(locationsWithDistance)}
                style={{ height: '100%', width: '100%', borderRadius: '4px' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {locationsWithDistance.filter((location) => location.lat && location.lon).map((location) => (
                  <Marker
                    key={location.id}
                    position={[location.lat, location.lon]}
                    icon={blueIcon}
                  >
                    <Popup>
                      <Box
                        sx={{
                          minWidth: 200,
                          m: '0 !important',
                          p: '0 !important',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={() => {
                          const addressParts = [
                            location.address.address1,
                            location.address.address2,
                            location.address.city,
                            location.address.state,
                            location.address.zip_code
                          ].filter(Boolean).join(', ');
                          const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
                          window.open(googleMapsUrl, '_blank');
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ m: '0 !important' }}>
                          {location.friendly_name || location.name}
                        </Typography>
                        <Typography variant="body2" sx={{ m: '0 !important' }}>
                          {location.address.address1}
                        </Typography>
                        {location.address.address2 && (
                          <Typography variant="body2" sx={{ m: '0 !important' }}>
                            {location.address.address2}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ m: '0 !important' }}>
                          {location.address.city}, {location.address.state} {location.address.zip_code}
                        </Typography>
                        {location.phone && (
                          <Typography variant="body2" sx={{ m: '0 !important', mt: '4px !important' }}>
                            📞 {location.phone}
                          </Typography>
                        )}
                        {location.distance && (
                          <Typography variant="caption" color="text.secondary" sx={{ m: '0 !important', mt: '4px !important', display: 'block' }}>
                            {location.distance.toFixed(1)} miles away
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ m: '0 !important', mt: '8px !important', display: 'block', color: 'primary.main' }}>
                          Click to open in Google Maps
                        </Typography>
                      </Box>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Box>

            {/* Locations List - Show only 6 closest */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {locationsWithDistance.slice(0, 6).map((location) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={location.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationOnIcon color="primary" sx={{ mt: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {location.friendly_name || location.name}
                          </Typography>
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {location.address.address1}
                            </Typography>
                            {location.address.address2 && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {location.address.address2}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {location.address.city}, {location.address.state} {location.address.zip_code}
                            </Typography>
                          </Box>
                          {location.phone && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              📞 {location.phone}
                            </Typography>
                          )}
                          {location.distance && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {location.distance.toFixed(1)} miles away
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Redeem Warning Modal */}
        <Dialog
          open={redeemModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={countdown > 0}
        >
          <DialogTitle>
            <Typography variant="h6" color={redeemSuccess ? 'primary.main' : 'default'}>
              {redeemSuccess ? 'Coupon Redeemed Successfully. Show device to cashier.' : 'Redeem Offer'}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {redeemSuccess ? (
              // Success content
              <Box>

                {selectedOffer && (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {selectedOffer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MonetizationOnIcon sx={{ color: 'gold', mr: 0.5 }} />
                          <Typography variant="body1" fontWeight="bold">
                            {selectedOffer.point_value}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {selectedOffer.description}
                      </Typography>
                      {selectedOffer.cashier_directions && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1" fontWeight="bold">
                            Cashier Instructions:
                          </Typography>
                          <Typography variant="body2">
                            {selectedOffer.cashier_directions}
                          </Typography>
                        </Box>
                      )}
                      {selectedOffer.cashier_image && (
                        <Box>
                          <img sx={{ maxWidth: '100%' }} src={selectedOffer.cashier_image} />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  {redeemSuccess.redeemed_when && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Redeemed:</strong> {new Date(redeemSuccess.redeemed_when).toLocaleString()}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>
                    <strong>Current Time:</strong> {currentTime.toLocaleString()}
                  </Typography>
                </Box>
                {countdown > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    You can close this window in {countdown} seconds
                  </Typography>
                )}
              </Box>
            ) : (
              // Warning/Error content
              <>
                <Typography variant="body1" color="error" fontWeight="bold" gutterBottom>
                  This coupon MUST be redeemed in front of the cashier for this coupon to be valid
                </Typography>
                {selectedOffer && (
                  <Box sx={{ mt: 2 }}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="div">
                            {selectedOffer.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {!!selectedOffer.point_value && (<>
                              <MonetizationOnIcon sx={{ color: 'gold', mr: 0.5 }} />
                              <Typography variant="body1" fontWeight="bold">
                                {selectedOffer.point_value}
                              </Typography>
                            </>)}
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {selectedOffer.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            {selectedOffer.days_remaining && (
                              <Typography variant="caption" color="warning.main" display="block">
                                {selectedOffer.days_remaining} days remaining. Expires on {new Date(selectedOffer.expire_when).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                    {!!selectedOffer.point_value && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Your account will be deducted {selectedOffer.point_value} coins
                      </Typography>
                    )}
                  </Box>
                )}
                {redeemError && (
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    Error: {redeemError}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {redeemSuccess ? (
              <Button
                variant="contained"
                onClick={handleCloseModal}
                disabled={countdown > 0}
              >
                {countdown > 0 ? `Close (${countdown})` : 'Close'}
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseModal} disabled={redeeming}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmRedeem}
                  disabled={redeeming}
                >
                  {redeeming ? 'Redeeming...' : 'I Understand'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Leave Program Dialog */}
        <Dialog
          open={leaveDialogOpen}
          onClose={handleCloseLeaveDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" color="error">
              Leave Loyalty Program
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you wish to leave this loyalty program?  Your contact information will be removed from the program, and you will no longer receive any offers or rewards.
            </Typography>
            <Typography variant="body1" color="error" fontWeight="bold">
              You will immediately forfeit all rewards and coins earned.
            </Typography>
            {leaveError && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Error: {leaveError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLeaveDialog} disabled={leaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmLeave}
              disabled={leaving}
            >
              {leaving ? 'Leaving...' : 'Leave Program'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Leave Program Link */}
        {isMember && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              color="error"
              onClick={handleLeaveProgramClick}
              sx={{ fontSize: '0.875rem' }}
            >
              Leave Loyalty Program
            </Link>
          </Box>
        )}

        {/* Enrollment Modal */}
        <EnrollmentModal
          open={enrollmentModal.open}
          onClose={() => setEnrollmentModal({ open: false, location: null })}
          location={enrollmentModal.location}
          onSuccess={handleEnrollSuccess}
        />

      </Box>
    </Container>
  );
};

// Helper function to calculate bounds for all locations
const calculateMapBounds = (locations) => {
  if (!locations || locations.length === 0) return null;

  const lats = locations.filter(loc => loc.lat && loc.lon).map(loc => loc.lat);
  const lons = locations.filter(loc => loc.lat && loc.lon).map(loc => loc.lon);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  // Add some padding to the bounds
  const latPadding = (maxLat - minLat) * 0.1;
  const lonPadding = (maxLon - minLon) * 0.1;

  return [
    [minLat - latPadding, minLon - lonPadding],
    [maxLat + latPadding, maxLon + lonPadding]
  ];
};

// Helper function to calculate distance between two coordinates in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Radius of the Earth in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
};

const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

export default MembershipDetails;